/**
 * Where an analysis type comes from, for grouping the imagery panel.
 *
 * The panel used to group by agronomy category (Vegetation / Soil / Terrain /
 * Operations). This groups by the source of the data instead, which is how the
 * types are actually thought about: something a satellite took, something a lab
 * measured, or something a machine recorded.
 *
 * Topography sits under Satellite deliberately. It is terrain rather than
 * imagery, but it arrives via satellite, and a fourth section for one type costs
 * more than it explains.
 *
 * Same shape as crop-colors.ts: pin what is known, fall back predictably, so a
 * new analysis type never renders blank or unsectioned.
 */

export type AnalysisSourceId = 'satellite' | 'soil' | 'machine' | 'other';

export interface AnalysisSource {
  id: AnalysisSourceId;
  name: string;
  icon: string;
  /**
   * What one capture's individual maps are called, singular and plural.
   *
   * The API calls them property types, which is database vocabulary. Chemical
   * Compare already shows the same values to users as "Elements", so soil keeps
   * that word and everything else uses the neutral "layers" — an NDVI band is
   * not an element.
   */
  itemLabel: { one: string; many: string };
}

/**
 * Display order is the order of this array.
 *
 * No colour per source. The active tab takes --primary-dark like every other
 * selected control, so it re-themes per tenant; fixed hex values here did not,
 * and on the real screen the active tab was hard to pick out.
 */
export const ANALYSIS_SOURCES: AnalysisSource[] = [
  { id: 'satellite', name: 'Satellite imagery', icon: 'satellite_alt', itemLabel: { one: 'layer', many: 'layers' } },
  { id: 'soil', name: 'Soil analyses', icon: 'science', itemLabel: { one: 'element', many: 'elements' } },
  { id: 'machine', name: 'Machine data', icon: 'agriculture', itemLabel: { one: 'layer', many: 'layers' } },
  { id: 'other', name: 'Other analyses', icon: 'layers', itemLabel: { one: 'layer', many: 'layers' } }
];

/**
 * Types that come back from the availability endpoint but are not overlays.
 *
 * CHEMICAL COMPARE is a comparison screen, not a layer. Four other consumers
 * filter it out by alias the same way — chemical-compare, field.component,
 * chemical-compare-rework and maps-chemical-compare — so leaving it in would
 * put a card on screen that cannot apply anything.
 */
const EXCLUDED_ALIASES = ['CHEMICAL COMPARE'];

/**
 * Types that are a flow rather than a layer.
 *
 * Profitability and VRT do not have a capture to show until you make one:
 * profitability needs an input cost and a product price, VRT needs a base map
 * and a rate per legend band. "View latest" is meaningless for both.
 *
 * They also have to stay reachable with no data at all — the legacy dialog has
 * an explicit disabled-exception so a VrtCreator can always enter — so they are
 * pinned in their tab rather than falling into the no-data footer.
 */
export type AnalysisFlow = 'vrt' | 'profitability';

/**
 * No label here on purpose. A flow card shows the same alias every other card
 * shows, so GENERATED_VRT reads as "Generated VRT" and PROFITABILITY as
 * "Profitability": the legacy dialog's words, only title-cased by
 * displayTypeName. This screen is a restyle rather than a rename: a user who
 * knows the old dialog has to recognise the entry here, and "Variable rate
 * maps" is not a term they have ever been shown.
 */
export interface FlowDefinition {
  flow: AnalysisFlow;
  /** Any one of these roles grants access. */
  roles: string[];
  icon: string;
}

const FLOW_TYPES: { match: string; definition: FlowDefinition }[] = [
  {
    match: 'GENERATED_VRT',
    definition: { flow: 'vrt', roles: ['VrtCreator', 'VrtDownloader'], icon: 'tune' }
  },
  {
    match: 'PROFITABILITY',
    definition: { flow: 'profitability', roles: ['ProfitabilityMapView'], icon: 'payments' }
  }
];

/** The flow a type opens, or null when it is an ordinary overlay. */
export function flowForType(type: string, alias: string): FlowDefinition | null {
  const name = `${(type || '').toUpperCase()} ${(alias || '').toUpperCase()}`;
  return FLOW_TYPES.find(f => name.includes(f.match))?.definition ?? null;
}

export function isSelectableType(type: string, alias: string): boolean {
  return !EXCLUDED_ALIASES.includes((alias || '').toUpperCase())
    && !EXCLUDED_ALIASES.includes((type || '').toUpperCase());
}

/**
 * Matched against the upper-cased type name, first match wins, so order matters
 * twice over: specific before general, and the CHEMICAL entries before any
 * looser SOIL match.
 *
 * The names here are confirmed against analysis_definition in all three
 * databases (Precision Gateway, AllFarm, Southern Farms), 2 September 2026.
 * Nutrient Index and Hot Spot are stored abbreviated as NI SATELLITE and HS
 * SATELLITE, so matching on the words NUTRIENT or HOT SPOT catches nothing —
 * those two entries are kept only as fallbacks against a rename.
 *
 * Differences between the three, all handled by the same list:
 *   AllFarm only          YARA NDVI SATELLITE, YARA NDVI SATELLITE FIXED,
 *                         S1 SATELLITE, SN SATELLITE
 *   Not in AllFarm        PROFITABILITY
 *   Precision Gateway     YIELD INTEGRATED alongside YIELD, which is aliased
 *                         YIELD (LEGACY) there
 */
const PINNED_SOURCES: { match: string; source: AnalysisSourceId; description: string; help?: string }[] = [
  // Yara's own NDVI is a distinct type, not a rename: it draws coverage from
  // Polaris rather than the premium provider, and only the non-fixed one offers
  // an imagery request. FIXED must precede it, being the longer name.
  // AllFarm only; neither exists in Precision Gateway or Southern Farms.
  // Fixed is a 0 to 1 scale in ten even bands (FixedLegendService.java); the other
  // stretches to each image. Every clear date produces one of each.
  {
    match: 'YARA NDVI SATELLITE FIXED', source: 'satellite', description: 'Yara crop vigour, fixed scale',
    help: 'Yara crop vigour on a fixed scale from 0 to 1: a colour means the same vigour on every field and every date, so fields and captures can be compared.'
  },
  {
    match: 'YARA NDVI SATELLITE', source: 'satellite', description: 'Yara crop vigour, contrast scale',
    help: 'Yara crop vigour, with the colours stretched to each image so that differences within the field stand out. Colours are not comparable between dates or fields.'
  },
  // The two crop-dependent Polaris indices. Both come from the same Sentinel-2
  // acquisition as Yara's NDVI, so they appear on the same dates, but each is
  // calibrated against the crop planted in the field. S1 is a relative vigour
  // scale with no unit; N-Uptake is a real quantity in kgN/ha. AllFarm only.
  {
    match: 'S1 SATELLITE', source: 'satellite', description: 'Crop biomass from satellite',
    help: 'How much crop is growing across the field, a better alternative to NDVI. Colours are relative to the crop.'
  },
  {
    match: 'SN SATELLITE', source: 'satellite', description: 'Nitrogen taken up by the crop',
    help: 'How much nitrogen the crop has taken up, in kg N/ha. Colours are relative to this field\'s own average on that date, so don\'t compare fields.'
  },
  {
    match: 'NDVI', source: 'satellite', description: 'Crop vigour from satellite',
    help: 'Crop vigour from satellite. NDVI compares the red and near-infrared light the crop reflects: a dense, healthy canopy scores high, bare soil and stressed crop score low.'
  },
  {
    match: 'NI SATELLITE', source: 'satellite', description: 'Nitrogen status estimate',
    help: 'Nitrogen status estimate from satellite. Shows where the crop looks short of nitrogen compared with the rest of the field.'
  },
  {
    match: 'NUTRIENT', source: 'satellite', description: 'Nitrogen status estimate',
    help: 'Nitrogen status estimate from satellite. Shows where the crop looks short of nitrogen compared with the rest of the field.'
  },
  {
    match: 'HS SATELLITE', source: 'satellite', description: 'Change and problem areas',
    help: 'Change and problem areas. Shows how crop vigour changed between the two most recent clear satellite images, so new problem areas stand out.'
  },
  {
    match: 'HOT SPOT', source: 'satellite', description: 'Change and problem areas',
    help: 'Change and problem areas. Shows how crop vigour changed between the two most recent clear satellite images, so new problem areas stand out.'
  },
  {
    match: 'TOPOGRAPHY', source: 'satellite', description: 'Elevation across the field',
    help: 'Elevation across the field, from satellite. Shows the high and low ground and the slopes in between.'
  },
  // Catch-all for anything else provider-prefixed. Legacy code treats a
  // "satellite" substring as the satellite signal, minus topography, and this
  // sits after the specific entries so they keep their own descriptions.
  // No help text: the type is unknown, so the "?" shows the description only.
  { match: 'SATELLITE', source: 'satellite', description: 'Satellite imagery' },
  {
    match: 'CHEMICAL - TOP SOIL', source: 'soil', description: 'Nutrient levels from lab samples',
    help: 'Nutrient levels from lab analysis of top soil samples. Pick an element to map it across the field.'
  },
  {
    match: 'CHEMICAL - SUB SOIL', source: 'soil', description: 'Sub soil nutrient levels',
    help: 'Nutrient levels from lab analysis of sub soil samples. Pick an element to map it across the field.'
  },
  {
    match: 'CHEMICAL', source: 'soil', description: 'Nutrient levels from lab samples',
    help: 'Nutrient levels from lab analysis of soil samples. Pick an element to map it across the field.'
  },
  {
    match: 'SOIL', source: 'soil', description: 'Soil measurements',
    help: 'Soil measurements for this field other than lab chemistry. Pick a layer to map it across the field.'
  },
  // GENERATED_VRT before VRT, being the longer name. The description stays
  // level-neutral: this is a creation flow at field level but an ordinary
  // viewable layer at farm level, so "Create a variable rate map" would sit
  // above a "View latest" button on the farm map and read as a broken button.
  {
    match: 'GENERATED_VRT', source: 'machine', description: 'Generated variable rate map',
    help: 'Variable rate maps generated from an imagery or soil layer, each with an application rate per zone.'
  },
  {
    match: 'VRT', source: 'machine', description: 'Variable rate application plan',
    help: 'A variable rate application plan for this field: the rate to apply in each zone.'
  },
  {
    match: 'AS APPLIED', source: 'machine', description: 'What the machine actually applied',
    help: 'What the machine actually applied, recorded by its controller during the operation.'
  },
  {
    match: 'YIELD', source: 'machine', description: 'Harvested yield per area',
    help: 'Harvested yield per area, recorded by the harvester\'s yield monitor.'
  },
  {
    match: 'PROFITABILITY', source: 'other', description: 'Profit and loss per area',
    help: 'Profit and loss per area across a field.'
  },
  // Present in all three databases with the alias MISC. Genuinely miscellaneous,
  // so Other is where it belongs rather than a forced fit.
  {
    match: 'MISCELLANEOUS', source: 'other', description: 'Uncategorised analyses',
    help: 'Analyses that do not fit one of the other categories.'
  }
];

function pinnedFor(type: string): { source: AnalysisSourceId; description: string; help?: string } | undefined {
  const name = (type || '').toUpperCase();
  return PINNED_SOURCES.find(p => name.includes(p.match));
}

/** The source an analysis type belongs to. Unknown types land in Other. */
export function sourceIdForType(type: string): AnalysisSourceId {
  return pinnedFor(type)?.source ?? 'other';
}

export function sourceById(id: AnalysisSourceId): AnalysisSource {
  return ANALYSIS_SOURCES.find(s => s.id === id) ?? ANALYSIS_SOURCES[ANALYSIS_SOURCES.length - 1];
}

/**
 * One plain line explaining what the type is, in sentence case.
 *
 * Empty string for an unknown type rather than invented text: a wrong
 * description is worse than none, and the card reads fine without one.
 */
export function descriptionForType(type: string): string {
  return pinnedFor(type)?.description ?? '';
}

/**
 * What the "?" next to a type's name explains, a sentence or two.
 *
 * Falls back to the one-line description for a type pinned only by a
 * catch-all, and to an empty string for an unknown type, which the panel reads
 * as "no ?" rather than inventing text.
 *
 * The texts are drafts until an agronomist has checked them (07/10/2026).
 */
export function helpForType(type: string): string {
  const pinned = pinnedFor(type);
  return pinned?.help || pinned?.description || '';
}

/**
 * Types the legacy MapDataFarm/MapDataField dialogs explain on hover, with the
 * same text as the "?" here. Only Yara's Biomass and N-Uptake, by request
 * (28/10/2026): the other types are left as they were on those screens.
 */
const LEGACY_HOVER_HELP_TYPES = ['S1 SATELLITE', 'SN SATELLITE'];

/** The legacy dialogs' hover text for a type, or '' for no tooltip. */
export function legacyHoverHelpForType(type: string | null | undefined): string {
  return LEGACY_HOVER_HELP_TYPES.includes((type || '').toUpperCase()) ? helpForType(type || '') : '';
}

/**
 * Acronyms that stay upper case when a name is title-cased.
 *
 * Kept short on purpose. NI and HS are how Nutrient Index and Hot Spot are
 * stored, see PINNED_SOURCES, and reading them as "Ni" and "Hs" would look like
 * a typo rather than a name.
 */
const TYPE_NAME_ACRONYMS = new Set(['NDVI', 'VRT', 'NI', 'HS']);

/**
 * An analysis type or alias in title case, for display.
 *
 * Names arrive upper case from the database, and a panel of upper-case card
 * titles reads as shouting and is slower to scan. The words are the stored
 * ones, only the casing changes, so a user who knows the legacy dialog still
 * recognises every entry.
 *
 * Works on runs of letters and digits rather than on whole words, so
 * "(LEGACY)" becomes "(Legacy)" and the punctuation around a run is left
 * exactly as stored.
 *
 * For type names only. Element names (propertyTypeName) carry chemical symbols
 * and units, which title-casing would corrupt, and so does a VRT caption like
 * "Lime (kg/ha)".
 */
export function displayTypeName(name: string | null | undefined): string {
  return (name || '').replace(/[A-Za-z0-9]+/g, run =>
    TYPE_NAME_ACRONYMS.has(run.toUpperCase())
      ? run.toUpperCase()
      : run.charAt(0).toUpperCase() + run.slice(1).toLowerCase()
  );
}
